import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import MusicToggle from '../components/MusicToggle.jsx'
import { getStoredToken, setStoredToken, getChildToken } from '../auth.js'
import { setLanguage, LANGUAGE_LABELS, SUPPORTED } from '../i18n.js'
import { api } from '../api.js'
import { KofiFloating } from '../components/KofiWidget.jsx'

function useReveal(threshold = 0.08) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    // If already in view on mount (e.g. after back-navigation), reveal immediately
    const rect = el.getBoundingClientRect()
    if (rect.top < window.innerHeight) {
      el.classList.add('visible')
      return
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible')
          obs.disconnect()
        }
      },
      { threshold }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return ref
}

function StepRow({ step, i, onZoom, t }) {
  const imgLeft = i % 2 === 0
  const refImg  = useReveal()
  const refText = useReveal()
  return (
    <div className={`flex flex-col gap-6 md:flex-row md:items-center md:gap-10 ${imgLeft ? '' : 'md:flex-row-reverse'}`}>
      {step.screenshot && (
        <div ref={refImg} className={`reveal w-full md:w-1/2 overflow-hidden rounded-2xl shadow-xl shadow-indigo-100/40 dark:shadow-black/40 border border-white/60 dark:border-white/5`}>
          <img
            src={step.screenshot}
            alt={step.screenshotAlt}
            className="hidden w-full object-cover object-top cursor-zoom-in md:block"
            style={{ maxHeight: '260px' }}
            onClick={() => onZoom(step.screenshot)}
          />
          <img
            src={step.screenshotMobile || step.screenshot}
            alt={step.screenshotAlt}
            className="w-full object-cover object-top cursor-zoom-in md:hidden"
            style={{ maxHeight: '220px' }}
            onClick={() => onZoom(step.screenshotMobile || step.screenshot)}
          />
        </div>
      )}
      <div ref={refText} className="reveal w-full md:w-1/2">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-500">{t('landing.how.step')} {i + 1}</p>
        <h3 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{step.title.replace(/^\d+\.\s*/, '')}</h3>
        <p className="mt-3 text-lg text-slate-600 dark:text-slate-300">{step.text}</p>
      </div>
    </div>
  )
}

function useParallax() {
  const ref = useRef(null)
  useEffect(() => {
    let rafId = null
    function onScroll() {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.style.transform = `translateY(${window.scrollY * 0.12}px)`
        }
        rafId = null
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [])
  return ref
}

function FlagIcon({ lang, size = 20 }) {
  const flags = {
    sv: (
      <svg width={size} height={size} viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="10" fill="#006AA7"/>
        <rect x="6" y="0" width="3" height="20" fill="#FECC02"/>
        <rect x="0" y="8.5" width="20" height="3" fill="#FECC02"/>
      </svg>
    ),
    no: (
      <svg width={size} height={size} viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="10" fill="#EF2B2D"/>
        <rect x="6" y="0" width="3.5" height="20" fill="#FFFFFF"/>
        <rect x="0" y="8.25" width="20" height="3.5" fill="#FFFFFF"/>
        <rect x="6.75" y="0" width="2" height="20" fill="#002868"/>
        <rect x="0" y="9" width="20" height="2" fill="#002868"/>
      </svg>
    ),
    da: (
      <svg width={size} height={size} viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="10" fill="#C60C30"/>
        <rect x="6" y="0" width="3" height="20" fill="#FFFFFF"/>
        <rect x="0" y="8.5" width="20" height="3" fill="#FFFFFF"/>
      </svg>
    ),
    fi: (
      <svg width={size} height={size} viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="20" rx="10" fill="#FFFFFF"/>
        <rect x="5" y="0" width="3.5" height="20" fill="#003580"/>
        <rect x="0" y="8.25" width="20" height="3.5" fill="#003580"/>
      </svg>
    ),
  }
  return flags[lang] ?? null
}

export default function Landing() {
  const { t, i18n } = useTranslation()
  const [currentLang, setCurrentLang] = useState(i18n.language || 'sv')
  function handleLangChange(lang) { setLanguage(lang); setCurrentLang(lang) }
  
  const howItWorks = t('landing.how.steps', { returnObjects: true })
  const familyFeatures = t('landing.features.items', { returnObjects: true })
  const privacyHighlights = t('landing.privacy.items', { returnObjects: true })
  const trustPoints = t('landing.trust.items', { returnObjects: true })
  
  const token = getStoredToken()
  const navigate = useNavigate()
  const [demoLoading, setDemoLoading] = useState(false)
  const [publicStats, setPublicStats] = useState(null)
  const [zoomedImg, setZoomedImg] = useState(null)
  const [langMenuOpen, setLangMenuOpen] = useState(false)

  const refStats = useReveal()
  const refFeatures = useReveal()
  const refMobile = useReveal()
  const refPrivacy = useReveal()
  const refCta = useReveal()
  const orbRef = useParallax()

  useEffect(() => {
    api.stats.public().then(setPublicStats).catch(() => {})
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
  }, [])

  useEffect(() => {
    if (zoomedImg === null) {
      document.body.style.overflow = ''
      return
    }
    document.body.style.overflow = 'hidden'
    function onKey(e) {
      if (e.key === 'Escape') setZoomedImg(null)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [zoomedImg])

  if (getChildToken()) return <Navigate to="/child" replace />
  if (token) return <Navigate to="/dashboard" replace />



  async function handleDemo() {
    setDemoLoading(true)
    try {
      const data = await api.auth.demo()
      setStoredToken(data.token)
      navigate('/dashboard')
    } catch {
      setDemoLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-sky-50 text-slate-900 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
       <header className="sticky top-0 z-30 border-b border-white/60 bg-white/80 backdrop-blur-lg dark:border-white/5 dark:bg-slate-900/70">
         <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-6">
           <Link to="/" className="flex items-center gap-2">
             <img src="/icons/logo.png" alt="Grannäventyret" className="h-10 w-auto object-contain" />
           </Link>
            <div className="flex items-center gap-1.5">
               <MusicToggle />
               {/* Mobil: aktiv flagga öppnar dropdown */}
               <div className="relative sm:hidden">
                 {langMenuOpen && (
                   <div className="fixed inset-0 z-40" onClick={() => setLangMenuOpen(false)} />
                 )}
                 <button
                   type="button"
                   onClick={() => setLangMenuOpen(v => !v)}
                   className="rounded-full px-1.5 py-1 text-base transition bg-slate-100 dark:bg-white/10"
                 >
                    <FlagIcon lang={currentLang} />
                 </button>
                 {langMenuOpen && (
                   <div className="absolute right-0 top-9 z-50 flex flex-col gap-1 rounded-xl border border-white/10 bg-slate-900 p-1.5 shadow-xl">
                     {SUPPORTED.filter(l => l !== currentLang).map(lang => (
                       <button
                         key={lang}
                         type="button"
                         onClick={() => { handleLangChange(lang); setLangMenuOpen(false) }}
                         className="rounded-lg px-3 py-1.5 text-left text-sm text-slate-200 hover:bg-white/10"
                       >
                          <span className="flex items-center gap-2">
                            <FlagIcon lang={lang} />
                            {lang === 'sv' ? 'Svenska' : lang === 'no' ? 'Norsk' : lang === 'da' ? 'Dansk' : 'Suomi'}
                          </span>
                       </button>
                     ))}
                   </div>
                 )}
               </div>

               {/* Desktop: alla flaggor synliga */}
               <div className="hidden sm:flex items-center gap-0.5">
                 {SUPPORTED.map((lang) => (
                   <button
                     key={lang}
                     type="button"
                     onClick={() => handleLangChange(lang)}
                     title={LANGUAGE_LABELS[lang]}
                     className={`rounded-full px-1.5 py-1 transition ${
                       currentLang === lang
                         ? 'bg-slate-100 font-semibold text-slate-800 dark:bg-white/10 dark:text-white'
                         : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                     }`}
                   >
                      <FlagIcon lang={lang} />
                   </button>
                 ))}
               </div>
               <Link to="/login" className="hidden sm:inline-flex rounded-full px-4 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white">
                 {t('landing.login')}
               </Link>
               <Link to="/register" className="rounded-full bg-gradient-to-r from-indigo-600 to-sky-500 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-indigo-500/40 hover:opacity-90 whitespace-nowrap">
                 {t('landing.createAccount')}
               </Link>
             </div>
         </div>
       </header>

      <main className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-0 mx-auto mt-10 h-64 max-w-6xl rounded-[46px] bg-gradient-to-r from-indigo-200/60 to-sky-200/60 blur-3xl dark:from-indigo-600/20 dark:to-sky-500/20" aria-hidden />
        <div
          ref={orbRef}
          className="pointer-events-none absolute right-0 top-96 z-0 h-72 w-72 rounded-full bg-gradient-to-br from-violet-300/40 to-pink-200/30 blur-3xl dark:from-violet-600/15 dark:to-pink-500/10"
          aria-hidden
        />

        {/* ── Hero ── */}
        <section className="relative z-10 px-4 pb-12 pt-10 md:px-6 md:pb-16 md:pt-12">
          <div className="mx-auto grid max-w-6xl gap-8 rounded-[40px] bg-white/70 p-6 shadow-2xl shadow-indigo-500/10 ring-1 ring-white/50 backdrop-blur-xl dark:bg-slate-900/80 dark:ring-white/5 md:p-8 lg:grid-cols-[1.1fr_0.9fr]">
             <div className="space-y-6">
               <p className="inline-flex items-center rounded-full bg-indigo-100/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-200">
                 {t('landing.hero.badge')}
               </p>
               <div>
                 <h1 className="text-4xl font-black text-slate-900 md:text-5xl dark:text-white">{t('landing.hero.title')}</h1>
                 <p className="mt-4 text-lg leading-relaxed text-slate-600 dark:text-slate-300">
                   {t('landing.hero.desc')}
                 </p>
               </div>
               <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                 <Link to="/register" className="rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/40 transition hover:bg-indigo-700 sm:px-6 sm:py-3 sm:text-base">
                   {t('landing.hero.createAccount')}
                 </Link>
                 <button
                   onClick={handleDemo}
                   disabled={demoLoading}
                   className="rounded-full border border-indigo-300 bg-indigo-50 px-5 py-2.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20 sm:px-6 sm:py-3 sm:text-base"
                 >
                   {demoLoading ? t('landing.hero.starting') : t('landing.hero.tryDemo')}
                 </button>
                </div>
                <a
                  href="#hur-det-fungerar"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-500 hover:text-indigo-700 dark:text-sky-400 dark:hover:text-sky-300 transition group"
                >
                  {t('landing.hero.howItWorks')}
                  <svg
                    className="h-4 w-4 transition-transform group-hover:translate-y-0.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
                </a>
             </div>

            {/* ── Responsive screenshot showcase ── */}

            {/* Mobile: single phone mockup */}
            <div className="relative flex justify-center lg:hidden">
              <div className="relative w-56">
                <div className="overflow-hidden rounded-[2.5rem] border-4 border-slate-800 shadow-2xl shadow-indigo-900/30 dark:border-slate-600">
                  {/* Notch — inbakad i skärmen */}
                  <div className="absolute left-1/2 top-1.5 z-10 h-3.5 w-16 -translate-x-1/2 rounded-full bg-slate-800 dark:bg-slate-900" />
                  <img src="/screenshots/dashboard-mobile.png" alt="Grannäventyret dashboard" className="w-full cursor-zoom-in" onClick={() => setZoomedImg('/screenshots/dashboard-mobile.png')} />
                </div>
              </div>
            </div>

            {/* Desktop: browser frame + overlapping phone */}
            <div className="relative hidden lg:flex lg:items-end lg:justify-center">
              {/* Glow */}
              <div className="absolute -left-6 -top-6 h-32 w-32 rounded-3xl bg-gradient-to-br from-sky-200 to-indigo-200 blur-3xl dark:from-indigo-700/40 dark:to-sky-600/30" aria-hidden />

              {/* Browser window mockup */}
              <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-white/70 shadow-2xl shadow-indigo-900/20 dark:border-slate-700">
                {/* Browser chrome bar */}
                <div className="flex items-center gap-1.5 border-b border-slate-200 bg-slate-100 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                  <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                  <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
                  <div className="ml-2 flex-1 rounded bg-white px-2 py-0.5 text-xs text-slate-400 dark:bg-slate-700 dark:text-slate-500">grannaventyret.se</div>
                </div>
                <img src="/screenshots/dashboard-desktop.png" alt="Grannäventyret dashboard" className="w-full object-cover object-top cursor-zoom-in" onClick={() => setZoomedImg('/screenshots/dashboard-desktop.png')} style={{ maxHeight: '340px' }} />
              </div>

              {/* Overlapping phone mockup */}
              <div className="absolute -bottom-4 -right-4 z-20 w-32">
                <div className="relative overflow-hidden rounded-[1.75rem] border-4 border-slate-800 shadow-2xl dark:border-slate-600">
                  {/* Notch — inbakad i skärmen */}
                  <div className="absolute left-1/2 top-1 z-10 h-2.5 w-10 -translate-x-1/2 rounded-full bg-slate-800 dark:bg-slate-900" />
                  <img src="/screenshots/dashboard-mobile.png" alt="Grannäventyret på mobil" className="w-full cursor-zoom-in" onClick={() => setZoomedImg('/screenshots/dashboard-mobile.png')} />
                </div>
              </div>
            </div>
          </div>
        </section>

         {/* ── Så fungerar det ── */}
         <section id="hur-det-fungerar" className="px-4 py-10 md:px-6 md:py-14">
           <div className="mx-auto max-w-5xl">
             <div className="mx-auto mb-10 max-w-2xl text-center">
               <p className="text-sm font-semibold uppercase tracking-[0.4em] text-indigo-500">{t('landing.how.label')}</p>
               <h2 className="mt-3 text-3xl font-bold text-slate-900 dark:text-white">{t('landing.how.title')}</h2>
             </div>
             <div className="flex flex-col gap-12 md:gap-16">
               {howItWorks.map((step, i) => (
                 <StepRow key={step.title} step={step} i={i} onZoom={setZoomedImg} t={t} />
               ))}
             </div>
           </div>
         </section>

         {/* ── Public stats ── */}
         {publicStats && publicStats.families > 0 && (
           <section className="mx-auto max-w-2xl px-4 py-6">
             <div className="mb-4 text-center">
               <p className="text-sm font-semibold uppercase tracking-[0.4em] text-indigo-500">{t('landing.stats.label')}</p>
               <h2 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{t('landing.stats.title')}</h2>
             </div>
             <div className="grid grid-cols-3 gap-4">
               <div className="rounded-2xl border border-slate-200/60 bg-white/60 px-4 py-5 text-center backdrop-blur-sm dark:border-white/10 dark:bg-white/5">
                 <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{publicStats.families}</p>
                 <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('landing.stats.families')}</p>
               </div>
               <div className="rounded-2xl border border-slate-200/60 bg-white/60 px-4 py-5 text-center backdrop-blur-sm dark:border-white/10 dark:bg-white/5">
                 <p className="text-2xl font-bold text-sky-600 dark:text-sky-400">{publicStats.quests_completed.toLocaleString('sv-SE')}</p>
                 <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('landing.stats.quests')}</p>
               </div>
               <div className="rounded-2xl border border-slate-200/60 bg-white/60 px-4 py-5 text-center backdrop-blur-sm dark:border-white/10 dark:bg-white/5">
                 <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{publicStats.total_xp.toLocaleString('sv-SE')}</p>
                 <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('landing.stats.xp')}</p>
               </div>
             </div>
           </section>
         )}

         {/* ── Feature grid ── */}
         <section className="px-4 py-10 md:px-6 md:py-14">
           <div className="mx-auto max-w-6xl">
             <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:gap-12">
               <div>
                 <p className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-500">{t('landing.features.label')}</p>
                 <h2 className="mt-3 text-3xl font-bold text-slate-900 dark:text-white">{t('landing.features.title')}</h2>
                 <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                   {t('landing.features.desc')}
                 </p>
               </div>
               <div ref={refFeatures} className="grid gap-4 sm:grid-cols-2 stagger">
                 {familyFeatures.map((feature) => (
                   <div key={feature.title} className="reveal rounded-2xl border border-white/70 bg-white/70 p-5 shadow-lg shadow-slate-200/60 backdrop-blur dark:border-white/5 dark:bg-slate-900/70 dark:shadow-black/40">
                     <p className="text-base font-semibold text-slate-900 dark:text-white">{feature.title}</p>
                     <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{feature.desc}</p>
                   </div>
                 ))}
               </div>
             </div>
           </div>
         </section>

         {/* ── Mobile showcase ── */}
         <section className="px-4 py-10 md:px-6 md:py-14">
           <div className="mx-auto max-w-6xl">
             <div ref={refMobile} className="grid gap-8 lg:grid-cols-2 lg:items-center">
               <div className="reveal-left">
                 <p className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-500">{t('landing.mobile.label')}</p>
                 <h2 className="mt-3 text-3xl font-bold text-slate-900 dark:text-white">{t('landing.mobile.title')}</h2>
                 <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">
                   {t('landing.mobile.desc')}
                 </p>
               </div>
               <div className="reveal-right flex justify-center">
                 {/* Desktop: browser frame */}
                 <div className="hidden w-full max-w-lg overflow-hidden rounded-2xl border border-white/70 shadow-2xl shadow-indigo-900/20 dark:border-slate-700 md:block">
                   <div className="flex items-center gap-1.5 border-b border-slate-200 bg-slate-100 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                     <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                     <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                     <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
                     <div className="ml-2 flex-1 rounded bg-white px-2 py-0.5 text-xs text-slate-400 dark:bg-slate-700 dark:text-slate-500">grannaventyret.se</div>
                   </div>
                   <img src="/screenshots/pwa-install-desktop.png" alt="Installera Grannäventyret som app" className="w-full cursor-zoom-in" onClick={() => setZoomedImg('/screenshots/pwa-install-desktop.png')} />
                 </div>
                 {/* Mobile: phone frame */}
                 <div className="relative w-48 md:hidden">
                   <div className="relative overflow-hidden rounded-[2.5rem] border-4 border-slate-800 shadow-2xl shadow-indigo-900/30 dark:border-slate-600">
                     {/* Notch — inbakad i skärmen */}
                     <div className="absolute left-1/2 top-1.5 z-10 h-3.5 w-16 -translate-x-1/2 rounded-full bg-slate-800 dark:bg-slate-900" />
                     <img src="/screenshots/dashboard-mobile.png" alt="Grannäventyret på mobil" className="w-full cursor-zoom-in" onClick={() => setZoomedImg('/screenshots/dashboard-mobile.png')} />
                   </div>
                 </div>
               </div>
            </div>
          </div>
        </section>

         {/* ── Privacy ── */}
         <section className="px-4 py-10 md:px-6 md:py-14">
             <div ref={refPrivacy} className="reveal mx-auto max-w-6xl rounded-[36px] border border-slate-200 bg-slate-900/95 p-6 text-white shadow-2xl shadow-indigo-900/40 dark:border-slate-800 md:p-10">
             <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
               <div>
                 <p className="text-sm font-semibold uppercase tracking-[0.4em] text-sky-300">{t('landing.privacy.label')}</p>
                 <h2 className="mt-3 text-3xl font-bold">{t('landing.privacy.title')}</h2>
               </div>
               <p className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80">
                 {t('landing.privacy.subtitle')}
               </p>
             </div>
             <div className="mt-8 grid gap-6 md:grid-cols-3">
               {privacyHighlights.map((item) => (
                 <div key={item.title} className="rounded-3xl bg-white/5 p-6">
                   <h3 className="text-xl font-semibold">{item.title}</h3>
                   <p className="mt-3 text-white/80">{item.text}</p>
                 </div>
               ))}
             </div>
           </div>
         </section>

         {/* ── Trust promises ── */}
         <section className="px-4 pb-16 md:px-6">
           <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-r from-indigo-600 to-sky-500 p-8 text-white shadow-2xl shadow-indigo-800/30">
             <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
               <h2 className="text-2xl font-semibold">{t('landing.trust.title')}</h2>
               <div className="grid gap-3 text-base text-white/85 md:grid-cols-3">
                 {trustPoints.map((point) => (
                   <p key={point} className="rounded-2xl bg-white/15 px-4 py-3">{point}</p>
                 ))}
               </div>
             </div>
           </div>
         </section>

         {/* ── CTA ── */}
         <section className="px-4 pb-20 md:px-6">
           <div ref={refCta} className="reveal mx-auto max-w-4xl rounded-[32px] border border-white/70 bg-white/80 p-10 text-center shadow-2xl shadow-indigo-100/60 backdrop-blur dark:border-white/5 dark:bg-slate-900/80 dark:shadow-black/40">
             <p className="text-sm font-semibold uppercase tracking-[0.4em] text-indigo-500">{t('landing.cta.label')}</p>
             <h2 className="mt-4 text-3xl font-bold text-slate-900 dark:text-white">{t('landing.cta.title')}</h2>
             <p className="mt-3 text-lg text-slate-600 dark:text-slate-300">{t('landing.cta.desc')}</p>
             <div className="mt-6 flex flex-wrap justify-center gap-3">
               <Link to="/register" className="rounded-full bg-indigo-600 px-6 py-3 font-semibold text-white shadow-lg shadow-indigo-500/40 hover:bg-indigo-700">
                 {t('landing.cta.createAccount')}
               </Link>
               <button
                 onClick={handleDemo}
                 disabled={demoLoading}
                 className="rounded-full border border-indigo-300 bg-indigo-50 px-6 py-3 font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20"
               >
                 {demoLoading ? t('landing.hero.starting') : t('landing.cta.tryDemo')}
               </button>
             </div>
           </div>
         </section>

         <footer className="px-4 pb-10 text-center md:px-6">
           <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-4">
             <Link to="/privacy" className="text-sm text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300">
               {t('landing.footer.privacy')}
             </Link>
             <span className="text-slate-300 dark:text-slate-700">·</span>
             <button
               onClick={handleDemo}
               disabled={demoLoading}
               className="text-sm text-indigo-500 hover:text-indigo-700 disabled:opacity-50 dark:text-indigo-400 dark:hover:text-indigo-300"
             >
               {demoLoading ? t('landing.hero.starting') : t('landing.footer.demo')}
             </button>
           </div>
         </footer>

        {/* Lightbox for hero images */}
        {zoomedImg !== null && createPortal(
          <>
            <style>{`
              @keyframes _mgZoomIn {
                from { opacity: 0; transform: scale(0.88); }
                to   { opacity: 1; transform: scale(1); }
              }
              ._mg-zoom { animation: _mgZoomIn 0.18s cubic-bezier(0.34,1.2,0.64,1) both; }
            `}</style>

            <div
              className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90"
              onClick={() => setZoomedImg(null)}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={() => setZoomedImg(null)}
                className="absolute right-4 top-4 z-10 rounded-full border border-white/20 bg-black/60 p-2.5 text-white shadow-lg backdrop-blur-sm transition hover:bg-black/80"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

               {/* Image */}
               <div
                 className="_mg-zoom flex items-center justify-center"
                 style={{ maxHeight: '75vh', maxWidth: '85vw' }}
               >
                <img
                  src={zoomedImg}
                  alt=""
                  className="cursor-zoom-out rounded-xl shadow-2xl"
                  style={{ maxHeight: '75vh', maxWidth: '85vw', objectFit: 'contain' }}
                />
              </div>

               {/* Close hint */}
               <p className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-4 py-1.5 text-xs font-medium text-white/80 backdrop-blur-sm pointer-events-none">
                 {t('landing.lightboxClose')}
               </p>
            </div>
          </>,
          document.body
        )}

      </main>
      <KofiFloating />
    </div>
  )
}
